/**
 * Tracking Helper for Google Ads, Meta (Facebook) Pixel, and TikTok Pixel
 *
 * This module provides helper functions for tracking conversions
 * for Glamping, Meteor, New Year, and Ramadan 2026 campaigns.
 *
 * UPDATE: Added dynamic TikTok pixel support for specific Ramadan pages.
 */

// Google Ads Conversion IDs
const GOOGLE_ADS_ID = 'AW-16843097853';

// Package-specific Google Ads conversion mappings
const PACKAGE_CONVERSIONS = {
  // Glamping Premium
  'Glamping-Mleiha---Premium-5737': `${GOOGLE_ADS_ID}/WyEpCKPts8wbEP2Ntd8-`,
  // Glamping Standard
  'Glamping-Mleiha----Standard-4611': `${GOOGLE_ADS_ID}/V5rqCLPFrcwbEP2Ntd8-`,
  'Mleiha-Glamping-Standard-Tent-December-31-2025-2654': `${GOOGLE_ADS_ID}/V5rqCLPFrcwbEP2Ntd8-`,
  // Meteor Premium (Overnight)
  'Geminids-Meteor-Shower-Overnight-Camping-Ticket-8397': `${GOOGLE_ADS_ID}/iqBHCPqGxMwbEP2Ntd8-`,
  // Meteor Standard
  'Mleiha-Geminids-Meteor-Shower-2025-5112': `${GOOGLE_ADS_ID}/u3ruCMbNvcwbEP2Ntd8-`,
  // New Year - no specific conversion, will use generic ViewContent only
  'New-Year-Under-the-Stars-Mleiha-National-Park-1979': null,
};

// Checkout conversion IDs
const CHECKOUT_CONVERSIONS = {
  checkout1: `${GOOGLE_ADS_ID}/8RrICKbts8wbEP2Ntd8-`, // AddToCart stage
  checkout2: `${GOOGLE_ADS_ID}/q0vgCKnts8wbEP2Ntd8-`, // Purchase stage
};

// Campaign-specific Purchase conversion IDs
const PURCHASE_CONVERSIONS = {
  // Glamping packages
  'Glamping-Mleiha---Premium-5737': `${GOOGLE_ADS_ID}/zhfwCNHMtdIbEP2Ntd8-`,
  'Glamping-Mleiha----Standard-4611': `${GOOGLE_ADS_ID}/zhfwCNHMtdIbEP2Ntd8-`,
  'Mleiha-Glamping-Standard-Tent-December-31-2025-2654': `${GOOGLE_ADS_ID}/zhfwCNHMtdIbEP2Ntd8-`,
  // New Year packages
  'New-Year-Under-the-Stars-Mleiha-National-Park-1979': `${GOOGLE_ADS_ID}/eDOdCNTMtdIbEP2Ntd8-`,
};

// Campaign listing page slugs (decoded)
const CAMPAIGN_LISTING_SLUGS = [
  'Glamping and Camping',
  'Mleiha Astronomy Events 2025',
  'Geminids Meteor Shower Event 2025',
  'RAMADAN 2026 – Iftar and Suhoor Under the Stars',
];

// Default TikTok Pixel ID (Global)
const DEFAULT_TIKTOK_PIXEL_ID = '';

// Ramadan 2026 Pixel ID
const RAMADAN_TIKTOK_PIXEL_ID = 'D66O0AJC77UBN50SM8E0';

// Current active TikTok Pixel ID (stateful)
let currentTikTokPixelId = null;

// Map of package slugs/URLs to specific TikTok Pixels
const PAGE_SPECIFIC_TIKTOK_PIXELS = {
  // Ramadan 2026 packages
  'Ramadan-Star-Lounge-2634': RAMADAN_TIKTOK_PIXEL_ID,
  'Ramadan-Star-Lounge-Suhoor-7586': RAMADAN_TIKTOK_PIXEL_ID,
  'Under-the-Stars:-Mleiha-Overnight-Iftar-&-Suhoor-6301': RAMADAN_TIKTOK_PIXEL_ID,
  'Ramadan-Lounge-2634': RAMADAN_TIKTOK_PIXEL_ID,
  // Listing page slugs (Checking generic logic in init function)
};

// Ramadan 2026 — TikTok custom event mapping (package slug → event name)
const RAMADAN_PACKAGE_TIKTOK_EVENTS = {
  'Ramadan-Star-Lounge-2634': 'Ramadan Star Lounge-Iftar',
  'Ramadan-Star-Lounge-Suhoor-7586': 'Ramadan Star Lounge-Suhoor',
  'Under-the-Stars:-Mleiha-Overnight-Iftar-&-Suhoor-6301': 'Mleiha Overnight Iftar and Suhoor',
};

// Ramadan 2026 — Google Ads conversion IDs
const RAMADAN_CHECKOUT_CONVERSION = `${GOOGLE_ADS_ID}/cE21CJzUnvcbEP2Ntd8-`;
const RAMADAN_PURCHASE_CONVERSION = `${GOOGLE_ADS_ID}/6sgXCJ_UnvcbEP2Ntd8-`;

// Meta Pixel ID for campaigns
const META_PIXEL_ID = '556522438278059';

// Page-specific Meta Pixel configurations
const PAGE_SPECIFIC_PIXELS = {
  'Hike-with-Ajmal-6602': '568370940464269',
};

// Ramadan 2026 — Exclusive Meta Pixel for BTB campaign
const RAMADAN_META_PIXEL_ID = '1434553225044755';

// Ramadan 2026 — Meta Pixel entry page paths (ONLY these pages activate the pixel)
const RAMADAN_META_ENTRY_PAGES = [
  '/p/Ramadan-BTB-2026',
  '/t/RAMADAN 2026 \u2013 Iftar and Suhoor Under the Stars',
  '/d/Sharjah-Boats-5633',
];

// sessionStorage keys for Ramadan Meta Pixel
const RAMADAN_META_SESSION_KEY = 'ramadan_meta_pixel_active';
const RAMADAN_META_SLUG_KEY = 'ramadan_meta_pixel_slug';
const RAMADAN_META_PURCHASE_PREFIX = 'ramadan_meta_purchase_fired_';

// B2C Always On 2026 - Mleiha National Park route-scoped funnel
const MLEIHA_B2C_ROUTE_PATH = '/d/MleihaNationalPark';
const MLEIHA_B2C_GOOGLE_PAGE_VIEW_CONVERSION = `${GOOGLE_ADS_ID}/GBnBCO_A8q4cEP2Ntd8-`;
const MLEIHA_B2C_GOOGLE_LEAD_CONVERSION = `${GOOGLE_ADS_ID}/T7apCO-W8q4cEP2Ntd8-`;
const MLEIHA_B2C_GOOGLE_PURCHASE_CONVERSION = `${GOOGLE_ADS_ID}/sXYCCOK1i68cEP2Ntd8-`;
const MLEIHA_B2C_SESSION_KEY = 'mleiha_b2c_pixel_active';
const MLEIHA_B2C_PAGE_VIEW_FIRED_KEY = 'mleiha_b2c_page_view_fired';
const MLEIHA_B2C_LEAD_FIRED_KEY = 'mleiha_b2c_lead_fired';
const MLEIHA_B2C_PURCHASE_PREFIX = 'mleiha_b2c_purchase_fired_';
const PENDING_PACKAGE_PURCHASE_TRACKING_KEY = 'pending_package_purchase_tracking';
const PACKAGE_PURCHASE_TRACKING_FIRED_PREFIX = 'package_purchase_tracking_fired_';
const MLEIHA_B2C_DESTINATION_IDS = new Set(['1']);

// Track whether the Ramadan Meta Pixel has been initialized in this page lifecycle
let ramadanMetaPixelInitialized = false;

// Track which events have been fired to prevent duplicates
const firedEvents = new Set();

// Track which page-specific pixels have been initialized
const initializedPagePixels = new Set();

const TRACKING_DEBUG_STORAGE_KEY = 'tracking_debug';
const PRODUCTION_TRACKING_HOSTS = [
  'discovershurooq.ae',
  'www.discovershurooq.ae',
];

const isProductionTrackingHost = () => {
  return typeof window !== 'undefined' &&
    PRODUCTION_TRACKING_HOSTS.includes(window.location.hostname);
};

const isProductionBuild = () => {
  return typeof process !== 'undefined' &&
    process.env &&
    process.env.NODE_ENV === 'production';
};

const isTrackingLoggingEnabled = () => {
  if (typeof window !== 'undefined') {
    try {
      const override = window.localStorage.getItem(TRACKING_DEBUG_STORAGE_KEY);
      if (override === 'true') return true;
      if (override === 'false') return false;
    } catch {}
  }

  return !isProductionBuild() && !isProductionTrackingHost();
};

const trackingLog = (...args) => {
  if (isTrackingLoggingEnabled()) {
    console.log(...args);
  }
};

const trackingWarn = (...args) => {
  if (isTrackingLoggingEnabled()) {
    console.warn(...args);
  }
};

/**
 * Debug logger for tracking events
 */
const debugLog = (source, eventType, data = {}) => {
  if (!isTrackingLoggingEnabled()) return;
  
  const timestamp = new Date().toISOString();
  
  trackingLog(`%c[TRACKING DEBUG - ${source}]`, 'background: #ff6b6b; color: white; padding: 2px 6px; border-radius: 3px;', {
    timestamp,
    eventType,
    data,
    currentTikTokPixel: currentTikTokPixelId,
    url: window.location.href,
  });
};

/**
 * Check if gtag is available
 */
const isGtagAvailable = () => {
  return typeof window !== 'undefined' && typeof window.gtag === 'function';
};

const ensureGtag = () => {
  if (typeof window === 'undefined') return null;

  window.dataLayer = window.dataLayer || [];

  if (!isGtagAvailable()) {
    window.gtag = function gtag() {
      window.dataLayer.push(arguments);
    };
    debugLog('trackingHelper.js', 'Google Ads gtag stub created from dataLayer', {});
  }

  return window.gtag;
};

/**
 * Check if fbq (Meta Pixel) is available
 */
const isFbqAvailable = () => {
  return typeof window !== 'undefined' && typeof window.fbq === 'function';
};

/**
 * Check if TikTok Pixel (ttq) is available
 */
const isTtqAvailable = () => {
  return typeof window !== 'undefined' && typeof window.ttq !== 'undefined';
};

/**
 * Initialize TikTok Pixel based on the current page/package
 * This should be called on route changes
 * @param {string} pathOrSlug - Current URL path or package slug
 */
export const initTikTokPixel = (pathOrSlug) => {
  if (!isTtqAvailable()) return;

  // Default to null - we only want to fire for specific Ramadan campaigns now
  let newPixelId = null;
  let decodedPath = '';
  
  try {
    decodedPath = decodeURIComponent(pathOrSlug || window.location.pathname);
  } catch (e) {
    decodedPath = pathOrSlug || '';
  }

  // Check if current path matches any specific pixel rules
  // 1. Direct Slug Match
  if (PAGE_SPECIFIC_TIKTOK_PIXELS[pathOrSlug]) {
    newPixelId = PAGE_SPECIFIC_TIKTOK_PIXELS[pathOrSlug];
  } 
  // 2. Path contains Ramadan slug key
  else if (Object.keys(PAGE_SPECIFIC_TIKTOK_PIXELS).some(slug => decodedPath.includes(slug))) {
    newPixelId = RAMADAN_TIKTOK_PIXEL_ID;
  }
  // 3. Fallback: Specific listing page check for Ramadan
  else if (decodedPath.includes('RAMADAN 2026') || decodedPath.includes('Ramadan-Star-Lounge')) {
    newPixelId = RAMADAN_TIKTOK_PIXEL_ID;
  }

  // If no pixel ID identified for this route, reset current ID and return
  if (!newPixelId) {
    debugLog('trackingHelper.js', 'TikTok Pixel SKIPPED (Not a Ramadan route)', { path: decodedPath });
    currentTikTokPixelId = null; // Reset current pixel ID to null so no events fire
    return;
  }

  if (currentTikTokPixelId !== newPixelId) {
    debugLog('trackingHelper.js', 'TikTok Pixel SWITCH', { from: currentTikTokPixelId, to: newPixelId, path: decodedPath });
  }

  currentTikTokPixelId = newPixelId;
  
  // Always load/ensure the current required pixel is active in the ttq stack
  window.ttq.load(newPixelId);
  window.ttq.page(); // Fire page view for this specific pixel
};

/**
 * Fire a TikTok Pixel event
 * @param {string} eventName - The event name (standard or custom)
 * @param {object} data - Event data (contents, value, currency, etc.)
 */
export const fireTikTokEvent = (eventName, data = {}) => {
  if (!currentTikTokPixelId) {
    debugLog('trackingHelper.js', `TikTok ${eventName} SKIPPED (No active pixel)`, data);
    return;
  }

  debugLog('trackingHelper.js', `TikTok ${eventName} ATTEMPT [${currentTikTokPixelId}]`, data);
  
  if (!isTtqAvailable()) {
    trackingWarn('%c[TRACKING] ttq not available', 'color: orange;');
    return;
  }

  // Use instance() to target the specific pixel valid for this page
  // ttq.instance(id) returns a handle to that specific pixel
  const instance = window.ttq.instance(currentTikTokPixelId);
  
  // Fallback to global if instance returns something unexpected, but usually it works if loaded.
  const target = instance || window.ttq;

  if (Object.keys(data).length > 0) {
    target.track(eventName, data);
  } else {
    target.track(eventName);
  }
  debugLog('trackingHelper.js', `TikTok ${eventName} FIRED`, data);
};

/**
 * Check if a package slug belongs to a Ramadan 2026 campaign package
 * @param {string} packageSlug - The package URL slug
 * @returns {boolean}
 */
export const isRamadanPackage = (packageSlug) => {
  if (!packageSlug) return false;
  return Object.keys(RAMADAN_PACKAGE_TIKTOK_EVENTS).includes(packageSlug) || 
         Object.keys(PAGE_SPECIFIC_TIKTOK_PIXELS).includes(packageSlug) ||
         packageSlug.includes('Ramadan');
         // Added broad check for robustness
};

/**
 * Fire TikTok custom ViewContent event for a Ramadan package
 * Each package fires its specific custom event name.
 * Uses deduplication to prevent multiple fires.
 * @param {string} packageSlug - The package URL slug
 * @param {string} packageName - The package display name
 * @param {string} packageId - The package ID
 */
export const trackRamadanPackageView = (packageSlug, packageName = '', packageId = '') => {
  // Ensure correct pixel is active first locally if checking here,
  // but initTikTokPixel should have handled it on route change.
  // Just in case, we can force update state if we know for sure.
  if (PAGE_SPECIFIC_TIKTOK_PIXELS[packageSlug]) {
     if (currentTikTokPixelId !== PAGE_SPECIFIC_TIKTOK_PIXELS[packageSlug]) {
         currentTikTokPixelId = PAGE_SPECIFIC_TIKTOK_PIXELS[packageSlug];
         // Note: We aren't calling load() here, assuming it was done on page load/route change.
         // If this is called w/o route change, we might miss the 'load'. 
         // But typical flow is Route -> Init -> Component Render -> trackRamadanPackageView
     }
  }

  const tiktokEventName = RAMADAN_PACKAGE_TIKTOK_EVENTS[packageSlug];
  if (!tiktokEventName) return;

  const eventKey = `ramadan_view_${packageSlug}`;
  if (firedEvents.has(eventKey)) return;
  firedEvents.add(eventKey);

  fireTikTokEvent(tiktokEventName, {
    contents: [
      {
        content_id: packageId || packageSlug,
        content_type: 'product',
        content_name: packageName || packageSlug,
      },
    ],
  });
};

/**
 * Fire a Google Ads conversion event
 * @param {string} sendTo - The conversion ID in format 'AW-XXXXX/XXXXX'
 * @param {string} transactionId - Optional transaction ID for deduplication
 */
export const fireGoogleConversion = (sendTo, transactionId = '', value = null, currency = 'AED') => {
  debugLog('trackingHelper.js', 'Google Ads Conversion ATTEMPT', { sendTo, transactionId, value, currency });

  const gtag = ensureGtag();
  if (!gtag) {
    trackingWarn('%c[TRACKING] gtag/dataLayer not available', 'color: orange;');
    return false;
  }

  const conversionData = {
    send_to: sendTo,
  };

  if (transactionId) {
    conversionData.transaction_id = transactionId;
  }
  
  if (value !== null && value !== undefined) {
    conversionData.value = value;
    conversionData.currency = currency;
  }

  gtag('event', 'conversion', conversionData);
  trackingLog(`[GoogleAds] Conversion Fired -> send_to: ${sendTo}`, conversionData);
  debugLog('trackingHelper.js', 'Google Ads Conversion FIRED', conversionData);
  return true;
};

/**
 * Fire a Meta (Facebook) Pixel event
 * @param {string} eventName - The event name (e.g., 'ViewContent', 'AddToCart', 'Purchase')
 * @param {object} data - Optional event data
 */
export const fireMetaEvent = (eventName, data = {}) => {
  debugLog('trackingHelper.js', `Meta Pixel ${eventName} ATTEMPT`, data);
  
  if (!isFbqAvailable()) {
    trackingWarn('%c[TRACKING] fbq not available', 'color: orange;');
    return;
  }

  if (Object.keys(data).length > 0) {
    window.fbq('track', eventName, data);
  } else {
    window.fbq('track', eventName);
  }
  debugLog('trackingHelper.js', `Meta Pixel ${eventName} FIRED`, data);
};

/**
 * Get Google Ads conversion ID for a package slug
 * @param {string} packageSlug - The package URL slug
 * @returns {string|null} - The conversion ID or null if not a tracked package
 */
export const getPackageConversionId = (packageSlug) => {
  if (!packageSlug) return null;
  return PACKAGE_CONVERSIONS[packageSlug] || null;
};

/**
 * Check if a package slug is a tracked campaign package
 * @param {string} packageSlug - The package URL slug
 * @returns {boolean}
 */
export const isTrackedPackage = (packageSlug) => {
  if (!packageSlug) return false;
  return Object.keys(PACKAGE_CONVERSIONS).includes(packageSlug);
};

/**
 * Check if a listing page slug is a tracked campaign listing
 * @param {string} slug - The listing page slug (URL decoded)
 * @returns {boolean}
 */
export const isTrackedListingPage = (slug) => {
  if (!slug) return false;
  const decodedSlug = decodeURIComponent(slug);
  return CAMPAIGN_LISTING_SLUGS.includes(decodedSlug);
};

/**
 * Fire tracking events for package click/view
 * Only fires Google Ads conversion (Meta ViewContent is handled by existing ReactPixel code)
 * Uses deduplication to prevent multiple fires on the same page
 * @param {string} packageSlug - The package URL slug
 */
export const trackPackageClick = (packageSlug) => {
  if (!isTrackedPackage(packageSlug)) {
    return;
  }

  // Deduplicate: only fire once per package per page session
  const eventKey = `package_click_${packageSlug}`;
  if (firedEvents.has(eventKey)) {
    // console.log('Tracking already fired for:', packageSlug);
    return;
  }
  firedEvents.add(eventKey);

  // NOTE: Meta ViewContent is NOT fired here - it's handled by existing ReactPixel.track() in the components
  // We only fire Google Ads conversion for campaign packages
  const conversionId = getPackageConversionId(packageSlug);
  if (conversionId) {
    fireGoogleConversion(conversionId);
  }
};

/**
 * Fire tracking events for listing page view
 * Only fires for campaign listing pages - NO Meta event (to avoid duplicates with existing code)
 * Uses deduplication to prevent multiple fires
 * @param {string} slug - The listing page slug
 */
export const trackListingPageView = (slug) => {
  if (!isTrackedListingPage(slug)) {
    return;
  }

  // Deduplicate: only fire once per listing page per session
  const eventKey = `listing_view_${slug}`;
  if (firedEvents.has(eventKey)) {
    // console.log('Listing tracking already fired for:', slug);
    return;
  }
  firedEvents.add(eventKey);

  // NOTE: We don't fire Meta ViewContent here to avoid duplicates
  // The listing pages don't need separate Meta tracking - only package detail pages do
  // console.log('Listing page tracking noted for:', slug);
};

/**
 * Fire Checkout 1 tracking events (AddToCart stage)
 * Uses deduplication to prevent multiple fires
 * @param {string} packageSlug - Optional package slug for context
 */
export const trackCheckout1 = (packageSlug = '') => {
  // Deduplicate: only fire once per checkout session
  const eventKey = `checkout1_${packageSlug || 'generic'}`;
  if (firedEvents.has(eventKey)) {
    return;
  }
  firedEvents.add(eventKey);

  // Fire Meta AddToCart
  fireMetaEvent('AddToCart');

  // Fire Google Ads Checkout 1 conversion
  fireGoogleConversion(CHECKOUT_CONVERSIONS.checkout1);

  // Fire TikTok InitiateCheckout
  fireTikTokEvent('InitiateCheckout', {
    contents: [
      {
        content_id: packageSlug || 'unknown',
        content_type: 'product',
        content_name: packageSlug || 'unknown',
      },
    ],
  });

  // Fire Ramadan-specific Google Ads Begin Checkout conversion
  if (isRamadanPackage(packageSlug)) {
    fireGoogleConversion(RAMADAN_CHECKOUT_CONVERSION);
  }
};

/**
 * Get Purchase conversion ID for a package slug (campaign-specific)
 * @param {string} packageSlug - The package URL slug
 * @returns {string|null} - The conversion ID or null if not a tracked package
 */
export const getPurchaseConversionId = (packageSlug) => {
  if (!packageSlug) return null;
  return PURCHASE_CONVERSIONS[packageSlug] || null;
};

/**
 * Fire campaign-specific Purchase conversion for Google Ads
 * @param {string} packageSlug - The package URL slug
 * @param {string} transactionId - The order/transaction ID for deduplication
 */
export const trackPurchaseConversion = (packageSlug, transactionId = '', value = null, currency = 'AED') => {
  const conversionId = getPurchaseConversionId(packageSlug);
  if (conversionId) {
    debugLog('trackingHelper.js', 'Campaign Purchase Conversion ATTEMPT', { packageSlug, conversionId, transactionId, value, currency });
    fireGoogleConversion(conversionId, transactionId, value, currency);
    // console.log('Campaign-specific Purchase conversion fired for:', packageSlug);
  }
};

/**
 * Get the page-specific Meta Pixel ID for a package slug
 * @param {string} packageSlug - The package URL slug
 * @returns {string|null} - The pixel ID or null if not configured
 */
export const getPageSpecificPixelId = (packageSlug) => {
  if (!packageSlug) return null;
  return PAGE_SPECIFIC_PIXELS[packageSlug] || null;
};

/**
 * Check if a package has a page-specific Meta Pixel
 * @param {string} packageSlug - The package URL slug
 * @returns {boolean}
 */
export const hasPageSpecificPixel = (packageSlug) => {
  if (!packageSlug) return false;
  return Object.keys(PAGE_SPECIFIC_PIXELS).includes(packageSlug);
};

/**
 * Initialize and fire page-specific Meta Pixel for a package
 * This creates a separate pixel instance for packages that have their own pixel ID
 * Uses deduplication to prevent multiple initializations
 * @param {string} packageSlug - The package URL slug
 */
export const initPageSpecificPixel = (packageSlug) => {
  if (!hasPageSpecificPixel(packageSlug)) {
    return;
  }

  const pixelId = getPageSpecificPixelId(packageSlug);
  if (!pixelId) return;

  // Deduplicate: only initialize once per page session
  const eventKey = `page_pixel_init_${packageSlug}`;
  if (initializedPagePixels.has(eventKey)) {
    debugLog('trackingHelper.js', 'Page-specific pixel already initialized', { packageSlug, pixelId });
    return;
  }
  initializedPagePixels.add(eventKey);

  debugLog('trackingHelper.js', 'Page-specific Meta Pixel INIT', { packageSlug, pixelId });

  if (typeof window !== 'undefined' && typeof document !== 'undefined') {
    // Initialize the pixel via fbq so it can track future events
    if (typeof window.fbq === 'function') {
      window.fbq('init', pixelId);
      debugLog('trackingHelper.js', 'Page-specific Meta Pixel initialized via fbq', { packageSlug, pixelId });
    }
    
    // Use direct image pixel approach to ensure PageView fires ONLY for this specific pixel
    const img = document.createElement('img');
    img.height = 1;
    img.width = 1;
    img.style.display = 'none';
    img.src = `https://www.facebook.com/tr?id=${pixelId}&ev=PageView&noscript=1`;
    document.body.appendChild(img);
    
    debugLog('trackingHelper.js', 'Page-specific Meta Pixel PageView FIRED via image', { packageSlug, pixelId });
  }
};

// --- Ramadan 2026 Exclusive Meta Pixel Functions ---

/**
 * Check if the Ramadan Meta Pixel session is active (user entered via a Ramadan campaign page).
 * @returns {boolean}
 */
export const isRamadanMetaPixelActive = () => {
  try {
    return sessionStorage.getItem(RAMADAN_META_SESSION_KEY) === 'true';
  } catch {
    return false;
  }
};

const activateRamadanMetaSession = (slug) => {
  try {
    sessionStorage.setItem(RAMADAN_META_SESSION_KEY, 'true');
    sessionStorage.setItem(RAMADAN_META_SLUG_KEY, slug);
  } catch {}
};

/**
 * Clear the Ramadan Meta Pixel session. Called after Purchase fires.
 */
export const clearRamadanMetaSession = () => {
  try {
    sessionStorage.removeItem(RAMADAN_META_SESSION_KEY);
    sessionStorage.removeItem(RAMADAN_META_SLUG_KEY);
  } catch {}
};

const hasRamadanMetaPurchaseFired = (orderNumber) => {
  try {
    return sessionStorage.getItem(RAMADAN_META_PURCHASE_PREFIX + orderNumber) === 'true';
  } catch {
    return false;
  }
};

const markRamadanMetaPurchaseFired = (orderNumber) => {
  try {
    sessionStorage.setItem(RAMADAN_META_PURCHASE_PREFIX + orderNumber, 'true');
  } catch {}
};

const normalizePath = (pathname = '') => {
  let decodedPath = '';
  try {
    decodedPath = decodeURIComponent(pathname || '');
  } catch {
    decodedPath = pathname || '';
  }

  const pathOnly = decodedPath.split('?')[0].split('#')[0];
  return pathOnly.length > 1 ? pathOnly.replace(/\/+$/, '') : pathOnly;
};

export const buildMleihaB2CTrackingContext = (...sources) => {
  const parts = [];

  const addPart = (value) => {
    if (value === null || value === undefined) return;
    if (typeof value === 'string' || typeof value === 'number') {
      const trimmedValue = String(value).trim();
      if (trimmedValue) parts.push(trimmedValue);
    }
  };

  sources.forEach((source) => {
    if (!source) return;

    if (typeof source === 'string' || typeof source === 'number') {
      addPart(source);
      return;
    }

    if (typeof source !== 'object') return;

    addPart(source.url_slug);
    addPart(source.slug);
    addPart(source.package_slug);
    addPart(source.package_name);
    addPart(source.package_arabic_name);
    addPart(source.name);
    addPart(source.arabic_name);
    addPart(source.destination_name);
    addPart(source.destination_slug);
    addPart(source.destinationName);
    addPart(source.dest_arabic_name);

    const destinationId = source.destination_id ?? source.destinationId ?? source.destination?.id;
    if (destinationId !== null &&
      destinationId !== undefined &&
      MLEIHA_B2C_DESTINATION_IDS.has(String(destinationId))) {
      addPart('Mleiha National Park');
      addPart('MleihaNationalPark');
    }

    if (typeof source.destination === 'string') {
      addPart(source.destination);
    } else {
      addPart(source.destination?.name);
      addPart(source.destination?.slug);
    }

    addPart(source.destinationData?.name);
    addPart(source.destinationData?.slug);
  });

  return parts.join(' ');
};

export const isMleihaB2CPixelActive = () => {
  try {
    return sessionStorage.getItem(MLEIHA_B2C_SESSION_KEY) === 'true';
  } catch {
    return false;
  }
};

const activateMleihaB2CSession = () => {
  try {
    sessionStorage.setItem(MLEIHA_B2C_SESSION_KEY, 'true');
  } catch {}
};

const removeMleihaB2CSessionKeysWithPrefix = (keyPrefix) => {
  try {
    for (let index = sessionStorage.length - 1; index >= 0; index -= 1) {
      const key = sessionStorage.key(index);
      if (key && key.startsWith(keyPrefix)) {
        sessionStorage.removeItem(key);
      }
    }
  } catch {}
};

export const clearMleihaB2CSession = () => {
  try {
    sessionStorage.removeItem(MLEIHA_B2C_SESSION_KEY);
    sessionStorage.removeItem(MLEIHA_B2C_PAGE_VIEW_FIRED_KEY);
    sessionStorage.removeItem(MLEIHA_B2C_LEAD_FIRED_KEY);
  } catch {}

  removeMleihaB2CSessionKeysWithPrefix(`${MLEIHA_B2C_PAGE_VIEW_FIRED_KEY}_`);
  removeMleihaB2CSessionKeysWithPrefix(`${MLEIHA_B2C_LEAD_FIRED_KEY}_`);
};

const getMleihaB2CContextKey = (baseKey, ...sources) => {
  const keySource = buildMleihaB2CTrackingContext(...sources) || MLEIHA_B2C_ROUTE_PATH;
  let hash = 0;

  for (let index = 0; index < keySource.length; index += 1) {
    hash = ((hash << 5) - hash) + keySource.charCodeAt(index);
    hash |= 0;
  }

  return `${baseKey}_${Math.abs(hash)}`;
};

const getMleihaB2CPageViewFiredKey = (pathname = '', trackingContext = '') => {
  return getMleihaB2CContextKey(MLEIHA_B2C_PAGE_VIEW_FIRED_KEY, pathname, trackingContext);
};

const hasMleihaB2CPageViewFired = (pathname = '', trackingContext = '') => {
  try {
    return sessionStorage.getItem(getMleihaB2CPageViewFiredKey(pathname, trackingContext)) === 'true';
  } catch {
    return false;
  }
};

const markMleihaB2CPageViewFired = (pathname = '', trackingContext = '') => {
  try {
    sessionStorage.setItem(MLEIHA_B2C_PAGE_VIEW_FIRED_KEY, 'true');
    sessionStorage.setItem(getMleihaB2CPageViewFiredKey(pathname, trackingContext), 'true');
  } catch {}
};

const getMleihaB2CLeadFiredKey = (trackingContext = '') => {
  return getMleihaB2CContextKey(MLEIHA_B2C_LEAD_FIRED_KEY, trackingContext);
};

const hasMleihaB2CLeadFired = (trackingContext = '') => {
  try {
    return sessionStorage.getItem(getMleihaB2CLeadFiredKey(trackingContext)) === 'true';
  } catch {
    return false;
  }
};

const markMleihaB2CLeadFired = (trackingContext = '') => {
  try {
    sessionStorage.setItem(MLEIHA_B2C_LEAD_FIRED_KEY, 'true');
    sessionStorage.setItem(getMleihaB2CLeadFiredKey(trackingContext), 'true');
  } catch {}
};

const hasMleihaB2CPurchaseFired = (orderNumber) => {
  if (!orderNumber) return false;
  try {
    return sessionStorage.getItem(MLEIHA_B2C_PURCHASE_PREFIX + orderNumber) === 'true';
  } catch {
    return false;
  }
};

const markMleihaB2CPurchaseFired = (orderNumber) => {
  if (!orderNumber) return;
  try {
    sessionStorage.setItem(MLEIHA_B2C_PURCHASE_PREFIX + orderNumber, 'true');
  } catch {}
};

const isMleihaB2CPathOrSlug = (value = '') => {
  const normalizedValue = normalizePath(value || '');
  const lowerValue = normalizedValue.toLowerCase();
  return normalizedValue === MLEIHA_B2C_ROUTE_PATH ||
    lowerValue.includes('mleiha') ||
    lowerValue.includes('geminids');
};

const hasMleihaB2CTrackingContext = (trackingContext = '') => {
  const explicitTrackingContext = buildMleihaB2CTrackingContext(trackingContext);

  if (explicitTrackingContext) {
    return isMleihaB2CPathOrSlug(explicitTrackingContext);
  }

  return isMleihaB2CPixelActive();
};

export const initMleihaB2CPixel = (pathname, trackingContext = '') => {
  const normalizedPath = normalizePath(pathname || (typeof window !== 'undefined' ? window.location.pathname : ''));
  const mleihaTrackingContext = buildMleihaB2CTrackingContext(normalizedPath, trackingContext);

  // Fire tracking if it's the main Mleiha entry page, OR if it's any page with mleiha/geminids in the URL 
  const isMleihaRoute = isMleihaB2CPathOrSlug(mleihaTrackingContext);

  // Activate session if they land on any Mleiha-related page
  if (isMleihaRoute) {
    activateMleihaB2CSession();
    debugLog('trackingHelper.js', 'Mleiha B2C Session Activated from route/context:', {
      path: normalizedPath,
      trackingContext,
    });
  } else {
    debugLog('trackingHelper.js', 'Mleiha B2C PageView SKIPPED (non-Mleiha route/context)', {
      path: normalizedPath,
      trackingContext,
    });
    return;
  }

  // Fire the Page View funnel event only on a Mleiha route/API context.
  if (hasMleihaB2CPageViewFired(normalizedPath, trackingContext)) {
    debugLog('trackingHelper.js', 'Mleiha B2C PageView SKIPPED (already fired for context)', {
      path: normalizedPath,
      trackingContext,
      pageViewKey: getMleihaB2CPageViewFiredKey(normalizedPath, trackingContext),
    });
    return;
  }
  const conversionQueued = fireGoogleConversion(MLEIHA_B2C_GOOGLE_PAGE_VIEW_CONVERSION);
  if (!conversionQueued) return;
  markMleihaB2CPageViewFired(normalizedPath, trackingContext);

  // Meta PageView/ViewContent for Mleiha routes fire once via the sitewide
  // tracking (pixel-init.js + ReactPixel) — re-firing them here double-counted
  // events on the main pixel.
};

export const fireMleihaB2CSubmitLead = (trackingContext = '') => {
  if (!hasMleihaB2CTrackingContext(trackingContext)) {
    debugLog('trackingHelper.js', 'Mleiha B2C SubmitLead SKIPPED (no active session or Mleiha context)', {
      trackingContext,
    });
    return;
  }

  if (hasMleihaB2CLeadFired(trackingContext)) {
    debugLog('trackingHelper.js', 'Mleiha B2C SubmitLead SKIPPED (already fired for context)', {
      trackingContext,
      leadKey: getMleihaB2CLeadFiredKey(trackingContext),
    });
    return;
  }

  if (!isMleihaB2CPixelActive() && isMleihaB2CPathOrSlug(trackingContext)) {
    activateMleihaB2CSession();
  }

  const conversionQueued = fireGoogleConversion(MLEIHA_B2C_GOOGLE_LEAD_CONVERSION);
  if (!conversionQueued) return;
  markMleihaB2CLeadFired(trackingContext);

  // Meta InitiateCheckout fires once via the sitewide checkout tracking.
  debugLog('trackingHelper.js', 'Mleiha B2C SubmitLead fired', {});
};

export const fireMleihaB2CPurchase = (value, orderNumber, currency = 'AED', trackingContext = '') => {
  if (!hasMleihaB2CTrackingContext(trackingContext)) return;

  if (hasMleihaB2CPurchaseFired(orderNumber)) return;

  if (!isMleihaB2CPixelActive() && isMleihaB2CPathOrSlug(trackingContext)) {
    activateMleihaB2CSession();
  }

  const conversionQueued = fireGoogleConversion(MLEIHA_B2C_GOOGLE_PURCHASE_CONVERSION, orderNumber, value, currency);
  if (!conversionQueued) return;
  markMleihaB2CPurchaseFired(orderNumber);

  // Meta Purchase fires once via the sitewide firePackagePurchaseTracking.
  clearMleihaB2CSession();

  debugLog('trackingHelper.js', 'Mleiha B2C Purchase fired + session cleared', { orderNumber });
};

/**
 * Initialize the Ramadan-exclusive Meta Pixel based on current route.
 * Called on every route change alongside initTikTokPixel.
 * If the path matches a Ramadan entry page: init pixel, fire PageView, activate session.
 * @param {string} pathname - The current location.pathname
 */
export const initRamadanMetaPixel = (pathname) => {
  let decodedPath = '';
  try {
    decodedPath = decodeURIComponent(pathname || '');
  } catch {
    decodedPath = pathname || '';
  }

  const isEntryPage = RAMADAN_META_ENTRY_PAGES.some((entryPath) => {
    let decodedEntry = '';
    try {
      decodedEntry = decodeURIComponent(entryPath);
    } catch {
      decodedEntry = entryPath;
    }
    return decodedPath === decodedEntry;
  });

  if (!isEntryPage) {
    debugLog('trackingHelper.js', 'Ramadan Meta Pixel route check (not entry page)', {
      path: decodedPath,
      sessionActive: isRamadanMetaPixelActive(),
    });
    return;
  }

  // Activate the session (always, so checkout/purchase gating works)
  activateRamadanMetaSession(decodedPath);

  // Only init pixel + fire PageView ONCE per page lifecycle to prevent
  // fbq('init') from replaying cached events on SPA back-navigation
  if (ramadanMetaPixelInitialized) {
    debugLog('trackingHelper.js', 'Ramadan Meta Pixel already initialized, skipping', { path: decodedPath });
    return;
  }
  ramadanMetaPixelInitialized = true;

  // Register the Ramadan pixel with fbq (if available)
  if (isFbqAvailable()) {
    window.fbq('init', RAMADAN_META_PIXEL_ID);
  }

  // Fire PageView using direct image pixel to ensure it fires even before the SDK fully loads
  // (same approach as initPageSpecificPixel)
  if (typeof window !== 'undefined' && typeof document !== 'undefined') {
    const img = document.createElement('img');
    img.height = 1;
    img.width = 1;
    img.style.display = 'none';
    img.src = `https://www.facebook.com/tr?id=${RAMADAN_META_PIXEL_ID}&ev=PageView&noscript=1`;
    document.body.appendChild(img);
  }

  debugLog('trackingHelper.js', 'Ramadan Meta Pixel INIT + PageView (image pixel)', {
    pixelId: RAMADAN_META_PIXEL_ID,
    path: decodedPath,
  });
};

/**
 * Fire InitiateCheckout on the Ramadan-exclusive Meta Pixel.
 * Only fires if the Ramadan session is active.
 * @param {string} packageSlug - The package slug for event data
 */
export const fireRamadanMetaInitiateCheckout = (packageSlug = '') => {
  if (!isRamadanMetaPixelActive()) return;
  if (!isFbqAvailable()) return;

  const eventKey = `ramadan_meta_checkout_${packageSlug || 'generic'}`;
  if (firedEvents.has(eventKey)) return;
  firedEvents.add(eventKey);

  // Ensure pixel is registered (safe no-op if already done)
  if (!ramadanMetaPixelInitialized) {
    window.fbq('init', RAMADAN_META_PIXEL_ID);
    ramadanMetaPixelInitialized = true;
  }

  window.fbq('trackSingle', RAMADAN_META_PIXEL_ID, 'InitiateCheckout', {
    content_name: packageSlug || 'Ramadan BTB 2026',
    content_category: 'Ramadan 2026',
    currency: 'AED',
  });

  debugLog('trackingHelper.js', 'Ramadan Meta InitiateCheckout FIRED', {
    pixelId: RAMADAN_META_PIXEL_ID,
    packageSlug,
  });
};

/**
 * Fire Purchase on the Ramadan-exclusive Meta Pixel.
 * Only fires if session is active and purchase hasn't fired for this order.
 * Clears the session after firing.
 * @param {number} value - Order total
 * @param {string} orderNumber - Order number for dedup
 * @param {string} currency - Currency code (default: 'AED')
 * @param {string} packageSlug - Package slug for event data
 */
export const fireRamadanMetaPurchase = (value, orderNumber, currency = 'AED', packageSlug = '') => {
  if (!isRamadanMetaPixelActive()) return;
  if (!isFbqAvailable()) return;
  if (hasRamadanMetaPurchaseFired(orderNumber)) return;

  // Ensure pixel is registered (needed after payment gateway redirect where in-memory state is lost)
  if (!ramadanMetaPixelInitialized) {
    window.fbq('init', RAMADAN_META_PIXEL_ID);
    ramadanMetaPixelInitialized = true;
  }

  window.fbq('trackSingle', RAMADAN_META_PIXEL_ID, 'Purchase', {
    value: value,
    currency: currency,
    content_name: packageSlug || 'Ramadan BTB 2026',
    content_category: 'Ramadan 2026',
  });

  markRamadanMetaPurchaseFired(orderNumber);
  clearRamadanMetaSession();

  debugLog('trackingHelper.js', 'Ramadan Meta Purchase FIRED + session cleared', {
    pixelId: RAMADAN_META_PIXEL_ID,
    value,
    orderNumber,
    packageSlug,
  });
};

const getTrackingOrderNumber = (source = {}) => {
  return source?.order_number ||
    source?.orderNumber ||
    source?.purchase_number ||
    source?.purchaseNumber ||
    '';
};

const getTrackingPackageSlug = (source = {}) => {
  return source?.url_slug ||
    source?.package_slug ||
    source?.slug ||
    source?.packageSlug ||
    '';
};

const getTrackingPackageName = (source = {}) => {
  return source?.package_name ||
    source?.packageName ||
    source?.name ||
    '';
};

const getTrackingValue = (source = {}, fallback = {}) => {
  const rawValue = source?.total ??
    source?.value ??
    source?.amount ??
    fallback?.total ??
    fallback?.value ??
    fallback?.amount ??
    0;

  const value = parseFloat(rawValue);
  return Number.isNaN(value) ? 0 : value;
};

const getTrackingCurrency = (source = {}, fallback = {}) => {
  return source?.currency || fallback?.currency || 'AED';
};

const hasPackagePurchaseTrackingFired = (orderNumber) => {
  if (!orderNumber) return false;
  try {
    return sessionStorage.getItem(PACKAGE_PURCHASE_TRACKING_FIRED_PREFIX + orderNumber) === 'true';
  } catch {
    return false;
  }
};

const markPackagePurchaseTrackingFired = (orderNumber) => {
  if (!orderNumber) return;
  try {
    sessionStorage.setItem(PACKAGE_PURCHASE_TRACKING_FIRED_PREFIX + orderNumber, 'true');
  } catch {}
};

export const markPendingPackagePurchaseTracking = (purchaseContext = {}) => {
  const orderNumber = getTrackingOrderNumber(purchaseContext);
  if (!orderNumber) return false;

  try {
    sessionStorage.setItem(PENDING_PACKAGE_PURCHASE_TRACKING_KEY, JSON.stringify({
      ...purchaseContext,
      order_number: orderNumber,
      purchase_number: purchaseContext.purchase_number || orderNumber,
    }));
    return true;
  } catch {
    return false;
  }
};

export const getPendingPackagePurchaseTracking = () => {
  try {
    const pendingPurchase = sessionStorage.getItem(PENDING_PACKAGE_PURCHASE_TRACKING_KEY);
    return pendingPurchase ? JSON.parse(pendingPurchase) : null;
  } catch {
    return null;
  }
};

export const clearPendingPackagePurchaseTracking = () => {
  try {
    sessionStorage.removeItem(PENDING_PACKAGE_PURCHASE_TRACKING_KEY);
  } catch {}
};

export const firePackagePurchaseTracking = (orderData = {}, fallbackContext = {}) => {
  const orderNumber = getTrackingOrderNumber(orderData) || getTrackingOrderNumber(fallbackContext);
  if (!orderNumber) return false;

  if (orderData?.status !== undefined && String(orderData.status) !== '1') {
    debugLog('trackingHelper.js', 'Package Purchase SKIPPED (order not successful)', {
      orderNumber,
      status: orderData.status,
    });
    return false;
  }

  if (hasPackagePurchaseTrackingFired(orderNumber)) {
    debugLog('trackingHelper.js', 'Package Purchase SKIPPED (already fired)', { orderNumber });
    return false;
  }

  const packageSlug = getTrackingPackageSlug(orderData) || getTrackingPackageSlug(fallbackContext);
  const packageName = getTrackingPackageName(orderData) || getTrackingPackageName(fallbackContext);
  const value = getTrackingValue(orderData, fallbackContext);
  const currency = getTrackingCurrency(orderData, fallbackContext);
  const contentName = packageName ? `${packageName} - Package` : packageSlug || orderNumber;
  const trackingContext = buildMleihaB2CTrackingContext(fallbackContext, orderData);

  fireMetaEvent('Purchase', {
    content_name: contentName,
    content_ids: packageSlug ? [`p-${packageSlug}`] : '',
    currency,
    content_category: 'product',
    content_type: 'product',
    value: `${value}`,
  });

  fireTikTokEvent('Purchase', {
    contents: [
      {
        content_id: packageSlug || orderNumber,
        content_type: 'product',
        content_name: packageName || packageSlug || orderNumber,
      },
    ],
    value,
    currency,
  });

  if (packageSlug) {
    trackPurchaseConversion(packageSlug, orderNumber, value, currency);
  }

  // Generic Google Ads Purchase-stage conversion with the real order number so
  // Google dedupes repeat fires (previously fired pre-payment on checkout click)
  fireGoogleConversion(CHECKOUT_CONVERSIONS.checkout2, orderNumber, value, currency);
  if (isRamadanPackage(packageSlug)) {
    fireGoogleConversion(RAMADAN_PURCHASE_CONVERSION, orderNumber, value, currency);
  }

  fireRamadanMetaPurchase(value, orderNumber, currency, packageSlug);
  fireMleihaB2CPurchase(value, orderNumber, currency, trackingContext);

  markPackagePurchaseTrackingFired(orderNumber);
  debugLog('trackingHelper.js', 'Package Purchase tracking fired', {
    orderNumber,
    packageSlug,
    packageName,
    value,
    currency,
  });

  return true;
};

/**
 * Find the order-history item that corresponds to a stored pending purchase.
 * The pending tracking number is the booking's purchase_number (stored under both
 * order_number and purchase_number). The order-history item carries BOTH an
 * order_number (UI booking ref, e.g. "BDXT1FUT") and a purchase_number
 * (e.g. "H9TGBWPI") — the pending tracking number matches the item's purchase_number.
 * @param {Array} orderHistoryData - The order history list from the orderHistory API
 * @param {object} pendingPurchase - The pending purchase context from sessionStorage
 * @returns {object|null} - The matching order, or null if none correlates
 */
export const matchPendingPurchaseToOrder = (orderHistoryData = [], pendingPurchase = {}) => {
  if (!Array.isArray(orderHistoryData) || !orderHistoryData.length) return null;
  if (!pendingPurchase) return null;

  const norm = (value) => (value === null || value === undefined ? '' : String(value).trim());

  // Pending tracking number = booking purchase_number (stored as order_number & purchase_number).
  const pendingTrackingNo = norm(getTrackingOrderNumber(pendingPurchase));
  if (!pendingTrackingNo) return null;

  // Confirmed correlation: order item's purchase_number === pending tracking number
  // (item.order_number is a different value, e.g. "BDXT1FUT"). order_number kept in the
  // same check only as a harmless safety net.
  return (
    orderHistoryData.find(
      (item) =>
        norm(item?.purchase_number) === pendingTrackingNo ||
        norm(item?.order_number) === pendingTrackingNo
    ) || null
  );
};

// Export constants for external use
export const TRACKING_CONFIG = {
  GOOGLE_ADS_ID,
  META_PIXEL_ID,
  RAMADAN_META_PIXEL_ID,
  MLEIHA_B2C_ROUTE_PATH,
  MLEIHA_B2C_GOOGLE_PAGE_VIEW_CONVERSION,
  MLEIHA_B2C_GOOGLE_LEAD_CONVERSION,
  MLEIHA_B2C_GOOGLE_PURCHASE_CONVERSION,
  DEFAULT_TIKTOK_PIXEL_ID,
  RAMADAN_TIKTOK_PIXEL_ID,
  PACKAGE_CONVERSIONS,
  CHECKOUT_CONVERSIONS,
  PURCHASE_CONVERSIONS,
  CAMPAIGN_LISTING_SLUGS,
  PAGE_SPECIFIC_PIXELS,
  RAMADAN_PACKAGE_TIKTOK_EVENTS,
  PENDING_PACKAGE_PURCHASE_TRACKING_KEY,
};

const trackingHelpers = {
  initTikTokPixel,
  fireGoogleConversion,
  fireMetaEvent,
  fireTikTokEvent,
  getPackageConversionId,
  isTrackedPackage,
  isTrackedListingPage,
  isRamadanPackage,
  trackPackageClick,
  trackListingPageView,
  trackRamadanPackageView,
  trackCheckout1,
  buildMleihaB2CTrackingContext,
  getPurchaseConversionId,
  trackPurchaseConversion,
  getPageSpecificPixelId,
  hasPageSpecificPixel,
  initPageSpecificPixel,
  initRamadanMetaPixel,
  isRamadanMetaPixelActive,
  clearRamadanMetaSession,
  fireRamadanMetaInitiateCheckout,
  fireRamadanMetaPurchase,
  markPendingPackagePurchaseTracking,
  getPendingPackagePurchaseTracking,
  clearPendingPackagePurchaseTracking,
  firePackagePurchaseTracking,
  matchPendingPurchaseToOrder,
  initMleihaB2CPixel,
  isMleihaB2CPixelActive,
  clearMleihaB2CSession,
  fireMleihaB2CSubmitLead,
  fireMleihaB2CPurchase,
  TRACKING_CONFIG,
};

export default trackingHelpers;
